import ms from "milsymbol";

import { SYMBOLS } from "./catalog";
import style from "./symbolStyle.json";

/** Dessin des annotations APP-6 par milsymbol (même source que le PDF : `tools/annotations/render_svgs.mjs` lit
 *  `symbolStyle.json` et applique les mêmes règles) :
 *  - fond du cadre : bleu ami #5CBDFF et rouge #DC7B7B des unités pour les identités ami et suspect/hostile ; jaune
 *    inconnu, vert neutre de milsymbol ; civils (ensemble 11) en violet (décision d'Alexis du 15/10/2026) ;
 *  - points de contrôle sans cadre (ensemble 25) d'une seule couleur : bleu ami, rouge ennemi, jaune inconnu ;
 *  - barrage routier : obstacle vert, dessiné à part (milsymbol ne le dessine pas en point).
 *  Sur la carte, contour blanc de 3 px comme les unités. */

export const ANNOTATION_PREFIX = "ann:";
// Taille des unités sur la carte (symbols.ts) : un cadre ami d'environ 49 px.
const SIZE = 33;

type Options = Record<string, unknown>;

export function annotationOptions(sidc: string, size = SIZE, outline = true): Options {
  const identity = sidc[3] ?? "1";
  const set = sidc.slice(4, 6);
  const options: Options = { size, ...(outline ? { outlineWidth: 3, outlineColor: "rgba(255,255,255,0.9)" } : {}) };
  if (set === "25") options.monoColor = (style.control as Record<string, string>)[identity] ?? style.control["3"];
  else if (set !== "11") {
    const fill = (style.fill as Record<string, string>)[identity];
    if (fill) options.fillColor = fill;
  }
  return options;
}

/** Barrage routier (obstacle, sans symbole ponctuel en APP-6D) : SVG du catalogue. */
function customSvg(sidc: string): string | null {
  for (const symbol of SYMBOLS.values()) {
    if (symbol.svg && sidc.slice(4, 6) === symbol.set && sidc.slice(10, 16) === symbol.entity) return symbol.svg;
  }
  return null;
}

/** SVG d'une annotation, pour la palette, la fiche et la liste ; `amplifier` : texte à droite (« PRESSE »). */
export function annotationSvg(sidc: string, size = 26, amplifier?: string | null): string {
  const custom = customSvg(sidc);
  if (custom) return custom.replace(/width="\d+" height="\d+"/, `width="${Math.round(size * 1.3)}" height="${Math.round(size * 0.8)}"`);
  return new ms.Symbol(sidc, { ...annotationOptions(sidc, size, false), ...(amplifier ? { additionalInformation: amplifier } : {}) }).asSVG();
}

/** Identifiant d'image sur la carte. */
export function annotationImageId(sidc: string, amplifier?: string | null): string {
  return `${ANNOTATION_PREFIX}${sidc}${amplifier ? `|${amplifier}` : ""}`;
}

/** Barrage routier dessiné au canevas (deux montants, une barre en travers), vert obstacle, liseré blanc. */
function roadblockImage(pixelRatio: number): ImageData {
  const w = Math.round(44 * pixelRatio);
  const h = Math.round(28 * pixelRatio);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D indisponible");
  ctx.scale(pixelRatio, pixelRatio);
  const draw = (color: string, posts: number, bar: number) => {
    ctx.strokeStyle = color;
    ctx.lineCap = "round";
    ctx.lineWidth = posts;
    ctx.beginPath();
    ctx.moveTo(7, 6);
    ctx.lineTo(7, 22);
    ctx.moveTo(37, 6);
    ctx.lineTo(37, 22);
    ctx.stroke();
    ctx.lineWidth = bar;
    ctx.beginPath();
    ctx.moveTo(7, 14);
    ctx.lineTo(37, 14);
    ctx.stroke();
  };
  draw("rgba(255,255,255,0.95)", 8, 10);
  draw(style.obstacle, 3.5, 5);
  return ctx.getImageData(0, 0, w, h);
}

/** Image d'une annotation pour la carte, recentrée sur son point d'ancrage (comme `symbolImage` des unités). */
export function annotationImage(sidc: string, pixelRatio: number, amplifier = ""): ImageData {
  if (customSvg(sidc)) return roadblockImage(pixelRatio);
  const symbol = new ms.Symbol(sidc, { ...annotationOptions(sidc), ...(amplifier ? { additionalInformation: amplifier } : {}) });
  const source = symbol.asCanvas(pixelRatio);
  const anchor = symbol.getAnchor();
  const ax = anchor.x * pixelRatio;
  const ay = anchor.y * pixelRatio;
  const width = Math.ceil(2 * Math.max(ax, source.width - ax));
  const height = Math.ceil(2 * Math.max(ay, source.height - ay));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D indisponible");
  ctx.drawImage(source, Math.round(width / 2 - ax), Math.round(height / 2 - ay));
  return ctx.getImageData(0, 0, width, height);
}

interface ImageHost {
  hasImage(id: string): boolean;
  addImage(id: string, image: ImageData, options?: { pixelRatio?: number }): void;
}

/** Ajoute à la carte, à la demande, l'image « ann:<sidc>[|texte] » ; vrai si l'identifiant est le sien. */
export function provideAnnotationImage(map: ImageHost, id: string): boolean {
  if (!id.startsWith(ANNOTATION_PREFIX)) return false;
  if (map.hasImage(id)) return true;
  const pixelRatio = Math.min(Math.max(window.devicePixelRatio || 1, 1), 3);
  const [sidc = "", amplifier = ""] = id.slice(ANNOTATION_PREFIX.length).split("|");
  map.addImage(id, annotationImage(sidc, pixelRatio, amplifier), { pixelRatio });
  return true;
}
